import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import type { Json } from '@/types/supabase'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FieldDef {
  key: string
  label: string
  type: 'text' | 'textarea' | 'select' | 'number'
  required: boolean
  options?: string[]
  placeholder?: string
}

export interface ChekiFormTemplate {
  id: string
  tenant_id: string
  name: string
  fields: FieldDef[]
  created_at: string
  updated_at: string
}

export interface UpsertChekiFormTemplateInput {
  name: string
  fields: FieldDef[]
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useChekiFormTemplates() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['cheki-form-templates', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cheki_form_templates')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as unknown as ChekiFormTemplate[]
    },
  })
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateChekiFormTemplate() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: UpsertChekiFormTemplateInput) => {
      const { data, error } = await supabase
        .from('cheki_form_templates')
        .insert({ ...input, fields: input.fields as unknown as Json, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data as unknown as ChekiFormTemplate
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cheki-form-templates', tenantId] }),
  })
}

export function useUpdateChekiFormTemplate() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, ...input }: UpsertChekiFormTemplateInput & { id: string }) => {
      const { error } = await supabase
        .from('cheki_form_templates')
        .update({ ...input, fields: input.fields as unknown as Json, updated_at: new Date().toISOString() })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cheki-form-templates', tenantId] }),
  })
}

export function useDeleteChekiFormTemplate() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cheki_form_templates')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cheki-form-templates', tenantId] }),
  })
}
